import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, RotateCcw } from 'lucide-react';
import client from '../../api/client';
import { useAuth, userIsSupervisor } from '../../context/AuthContext';
import { NAVY, AMBER, AMBER_DARK, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle } from '../../styles/tokens';

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie'];

const STATUS_CONFIG = {
  draft:     { label: 'Borrador',  color: '#7c3aed', bg: '#f3e8ff' },
  submitted: { label: 'Enviado',   color: '#ca8a04', bg: '#fef9c3' },
  approved:  { label: 'Aprobado',  color: '#16a34a', bg: '#dcfce7' },
  returned:  { label: 'Devuelto',  color: '#ea580c', bg: '#fed7aa' },
};

function fmtShort(dateStr) {
  return new Date(dateStr).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}

function addDays(dateStr, n) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function TimesheetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSup = userIsSupervisor(user);

  const [ts, setTs] = useState(null);
  const [notes, setNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get(`/api/timesheets/${id}`)
      .then(r => setTs(r.data))
      .catch(err => {
        console.error(err);
        navigate('/horas');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando...</div>;
  if (!ts) return null;

  const weekDates = Array.from({ length: 5 }, (_, i) => addDays(ts.week_start, i));
  const cfg = STATUS_CONFIG[ts.status] || STATUS_CONFIG.draft;
  const isReviewer = isSup && ts.user_id !== user.id;

  // Group entries by project
  const projectMap = {};
  (ts.entries || []).forEach(e => {
    const pName = e.project_name || 'Sin proyecto';
    if (!projectMap[pName]) projectMap[pName] = { hours: [0,0,0,0,0], descriptions: ['','','','',''] };
    const dayIdx = weekDates.indexOf(e.entry_date);
    if (dayIdx >= 0) {
      projectMap[pName].hours[dayIdx] = parseFloat(e.hours) || 0;
      projectMap[pName].descriptions[dayIdx] = e.description || '';
    }
  });

  const projectRows = Object.entries(projectMap);
  const dayTotals = weekDates.map((_, i) => projectRows.reduce((sum, [, r]) => sum + (r.hours[i] || 0), 0));

  async function handleApprove() {
    setActionLoading('approve');
    try {
      await client.post(`/api/timesheets/${id}/approve`, { notes: notes || null });
      const res = await client.get(`/api/timesheets/${id}`);
      setTs(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Error.');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleReturn() {
    if (!notes.trim()) {
      alert('Las notas son requeridas al devolver.');
      return;
    }
    setActionLoading('return');
    try {
      await client.post(`/api/timesheets/${id}/return`, { notes });
      const res = await client.get(`/api/timesheets/${id}`);
      setTs(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Error.');
    } finally {
      setActionLoading(null);
    }
  }

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 900, margin: '0 auto' }}>
      {/* Back */}
      <button onClick={() => navigate(-1)} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#64748b', fontSize: 13, marginBottom: 14,
        fontFamily: 'Inter, sans-serif',
      }}>
        <ArrowLeft size={16} /> Volver
      </button>

      {/* Header */}
      <div style={{ ...bubbleStyle, padding: '18px 22px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>
              Semana: {fmtShort(ts.week_start)} — {fmtShort(addDays(ts.week_start, 4))}
            </h1>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              {ts.user_name} · {parseFloat(ts.total_hours)} horas
            </div>
          </div>
          <span style={{
            padding: '4px 14px', borderRadius: 12,
            background: cfg.bg, color: cfg.color,
            fontSize: 12, fontWeight: 600,
          }}>
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Reviewer notes */}
      {ts.reviewer_notes && (
        <div style={{
          ...bubbleStyle, padding: '12px 18px', marginBottom: 16,
          borderLeft: `3px solid ${STATUS_CONFIG[ts.status]?.color || '#94a3b8'}`,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 2 }}>
            Notas de {ts.reviewer_name || 'Revisor'}:
          </div>
          <div style={{ fontSize: 13, color: NAVY }}>{ts.reviewer_notes}</div>
        </div>
      )}

      {/* Entries table */}
      <div style={{ ...bubbleStyle, padding: 0, overflow: 'auto', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600, color: NAVY, borderBottom: `1px solid ${BORDER_COLOR}` }}>
                Proyecto
              </th>
              {weekDates.map((d, i) => (
                <th key={i} style={{
                  textAlign: 'center', padding: '10px 6px', fontWeight: 600, color: NAVY,
                  borderBottom: `1px solid ${BORDER_COLOR}`, minWidth: 60,
                }}>
                  <div>{DAYS[i]}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 400 }}>{fmtShort(d)}</div>
                </th>
              ))}
              <th style={{ textAlign: 'center', padding: '10px 8px', fontWeight: 700, color: NAVY, borderBottom: `1px solid ${BORDER_COLOR}` }}>
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {projectRows.map(([pName, row]) => {
              const rowTotal = row.hours.reduce((a, b) => a + b, 0);
              return (
                <tr key={pName} style={{ borderBottom: `1px solid ${BORDER_COLOR}` }}>
                  <td style={{ padding: '10px 12px', fontWeight: 500, color: NAVY }}>{pName}</td>
                  {row.hours.map((h, i) => (
                    <td key={i} style={{
                      textAlign: 'center', padding: '8px 6px',
                      color: h > 0 ? NAVY : '#cbd5e1',
                    }}>
                      {h > 0 ? h.toFixed(1) : '—'}
                    </td>
                  ))}
                  <td style={{ textAlign: 'center', padding: '8px', fontWeight: 700, color: NAVY }}>
                    {rowTotal.toFixed(1)}
                  </td>
                </tr>
              );
            })}
            <tr style={{ background: '#f8fafc' }}>
              <td style={{ padding: '10px 12px', fontWeight: 700, color: NAVY, fontSize: 12 }}>Total Diario</td>
              {dayTotals.map((dt, i) => (
                <td key={i} style={{
                  textAlign: 'center', padding: '10px 6px', fontWeight: 700,
                  color: dt > 8 ? '#ef4444' : dt > 0 ? NAVY : '#cbd5e1',
                }}>
                  {dt.toFixed(1)}
                </td>
              ))}
              <td style={{ textAlign: 'center', padding: '10px 8px', fontWeight: 800, color: AMBER_DARK, fontSize: 14 }}>
                {parseFloat(ts.total_hours).toFixed(1)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Supervisor actions */}
      {isReviewer && ts.status === 'submitted' && (
        <div style={{ ...bubbleStyle, padding: '16px 20px' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: NAVY, marginBottom: 10 }}>Acciones de Revisión</div>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Notas (requeridas para devolver)..."
            rows={2}
            style={{ ...fieldStyle, marginBottom: 10, resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleApprove}
              disabled={!!actionLoading}
              style={{
                ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
                background: '#16a34a', padding: '8px 16px', fontSize: 13,
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              <CheckCircle2 size={14} /> Aprobar
            </button>
            <button
              onClick={handleReturn}
              disabled={!!actionLoading}
              style={{
                ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
                background: '#ea580c', padding: '8px 16px', fontSize: 13,
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              <RotateCcw size={14} /> Devolver
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
